import { useEffect, useRef, useState } from "react";
import AdminLayout from "@/components/AdminLayout";
import TimePicker from "@/components/TimePicker";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Pencil, Tv, QrCode, Trash2, Hash, Send, GripVertical, Download } from "lucide-react";
import api, { formatDetail } from "@/lib/api";
import { useAuth } from "@/lib/auth";

const WEEKDAYS = [
  { val: 0, label: "Mon" },
  { val: 1, label: "Tue" },
  { val: 2, label: "Wed" },
  { val: 3, label: "Thu" },
  { val: 4, label: "Fri" },
  { val: 5, label: "Sat" },
  { val: 6, label: "Sun" },
];

export default function ManageStaff() {
  const { user: me } = useAuth();
  const isOwner = me?.role === "owner";
  const [users, setUsers] = useState([]);
  const [services, setServices] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [orphans, setOrphans] = useState([]);
  const [selectedOrphans, setSelectedOrphans] = useState({});
  const [importPassword, setImportPassword] = useState("staff123");
  const [importBusy, setImportBusy] = useState(false);

  const load = async () => {
    const [u, s] = await Promise.all([api.get("/admin/users"), api.get("/public/services")]);
    setUsers(u.data.filter((x) => x.role === "staff")); setServices(s.data);
  };
  useEffect(() => { load(); }, []);

  const openImport = async () => {
    try {
      const { data } = await api.get("/admin/users/turnboard-orphans");
      setOrphans(data.orphans || []);
      setSelectedOrphans(Object.fromEntries((data.orphans || []).map((o) => [o.pager, true])));
      setImportOpen(true);
    } catch (e) { toast.error(formatDetail(e.response?.data?.detail)); }
  };

  const runImport = async () => {
    const pagers = Object.entries(selectedOrphans).filter(([, v]) => v).map(([k]) => k);
    if (pagers.length === 0) { toast.error("Pick at least one staff to import"); return; }
    setImportBusy(true);
    try {
      const { data } = await api.post("/admin/users/import-from-turnboard", {
        pagers, default_password: importPassword || "staff123",
      });
      const c = data.created?.length || 0;
      const sk = data.skipped?.length || 0;
      toast.success(`Imported ${c} staff${sk ? ` (skipped ${sk})` : ""}. Default password: ${data.default_password}`);
      setImportOpen(false); load();
    } catch (e) { toast.error(formatDetail(e.response?.data?.detail)); }
    finally { setImportBusy(false); }
  };

  const save = async (form) => {
    if (!(form.pager || "").trim() || !(form.telegram_id || "").trim()) {
      toast.error("Pager # and Telegram ID are required"); return;
    }
    try { await api.patch(`/admin/users/${editing.id}`, form); toast.success("Updated"); setOpen(false); load(); } catch (e) { toast.error(formatDetail(e.response?.data?.detail)); }
  };
  const create = async (form) => {
    if (!form.username.trim() || !form.password.trim() || !form.full_name.trim()) {
      toast.error("Username, password, and full name are required"); return;
    }
    if (!form.pager.trim() || !form.telegram_id.trim()) {
      toast.error("Pager # and Telegram ID are required"); return;
    }
    try { await api.post("/admin/users", { ...form, role: "staff" }); toast.success("Created — synced to Turn Board"); setCreateOpen(false); load(); } catch (e) { toast.error(formatDetail(e.response?.data?.detail)); }
  };
  const remove = async (u) => {
    if (!window.confirm(`Permanently delete "${u.full_name}"? This also removes them from the Turn Board.`)) return;
    try { await api.delete(`/admin/users/${u.id}`); toast.success("Removed"); load(); }
    catch (e) { toast.error(formatDetail(e.response?.data?.detail)); }
  };

  // Drag-to-reorder. Owners drag a staff card by the grip handle; new order is
  // persisted to /admin/users/reorder and immediately re-fetched so the
  // Booking calendar columns mirror this order on next load.
  const dragRef = useRef(null);
  const moveStaff = async (fromIdx, toIdx) => {
    if (fromIdx === toIdx || !isOwner) return;
    const next = [...users];
    const [m] = next.splice(fromIdx, 1);
    next.splice(toIdx, 0, m);
    // Optimistic local update so card visibly moves while saving
    setUsers(next.map((u, i) => ({ ...u, sort_order: i })));
    try {
      await api.post("/admin/users/reorder",
        next.map((u, i) => ({ id: u.id, sort_order: i })));
      toast.success("Staff order saved — Booking calendar updated");
    } catch (e) { toast.error(formatDetail(e.response?.data?.detail)); load(); }
  };

  return (
    <AdminLayout title="Staff & Skills" showTurnBoard={false}>
      <div className="flex flex-wrap items-center justify-between gap-2 mb-6">
        {isOwner && (
          <div className="text-xs text-zen-muted italic" data-testid="staff-reorder-hint">
            Drag the <GripVertical className="w-3 h-3 inline -mt-0.5" /> handle to reorder staff. This order is mirrored in the Booking calendar.
          </div>
        )}
        <div className="flex flex-wrap gap-2 ml-auto">
          <a href="/clock-in" target="_blank" rel="noreferrer"
            className="inline-flex items-center gap-1 h-10 px-4 rounded-full border border-zen-border bg-white text-sm text-zen-ink hover:border-zen-primary"
            data-testid="tech-clockin-link">
            <QrCode className="w-4 h-4" /> Tech Clock-in Page
          </a>
          <a href="/turn-board" target="_blank" rel="noreferrer"
            className="inline-flex items-center gap-1 h-10 px-4 rounded-full border border-zen-border bg-white text-sm text-zen-ink hover:border-zen-primary"
            data-testid="turnboard-tv-link">
            <Tv className="w-4 h-4" /> Turn Board TV (Owner)
          </a>
          {isOwner && (
            <Button onClick={openImport} variant="outline" className="rounded-full" data-testid="import-tb-btn">
              <Download className="w-4 h-4 mr-1" /> Import from Turn Board
            </Button>
          )}
          <Button onClick={() => setCreateOpen(true)} className="rounded-full bg-zen-primary text-white" data-testid="new-staff-btn"><Plus className="w-4 h-4 mr-1" /> Add Staff</Button>
        </div>
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        {users.map((u, idx) => (
          <div
            key={u.id}
            className="bg-white rounded-2xl border border-zen-border p-5 flex items-start gap-2"
            draggable={isOwner}
            onDragStart={() => { if (isOwner) dragRef.current = idx; }}
            onDragOver={(e) => { if (isOwner && dragRef.current !== null) e.preventDefault(); }}
            onDrop={(e) => {
              if (isOwner && dragRef.current !== null) {
                e.preventDefault();
                moveStaff(dragRef.current, idx);
                dragRef.current = null;
              }
            }}
            onDragEnd={() => { dragRef.current = null; }}
            data-testid={`staff-card-${u.id}`}>
            {isOwner && <GripVertical className="w-4 h-4 text-zen-muted mt-1 flex-shrink-0 cursor-grab" />}
            <div className="flex-1 min-w-0">
              <div className="flex justify-between items-start">
                <div>
                  <div className="font-serif text-xl text-zen-ink flex items-center gap-2">
                    {u.full_name}
                    {u.sex === "male" && <span className="text-xs px-2 py-0.5 rounded-full bg-zen-primary/15 text-zen-primary font-medium">♂ M</span>}
                    {u.sex === "female" && <span className="text-xs px-2 py-0.5 rounded-full bg-zen-cal-red/15 text-zen-cal-red font-medium">♀ F</span>}
                  </div>
                  <div className="text-xs text-zen-muted">@{u.username} · {u.active ? "Active" : "Inactive"}</div>
                  {(u.pager || u.telegram_id) && (
                    <div className="flex flex-wrap gap-2 mt-2 text-[11px]">
                      {u.pager && <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-zen-primary/10 text-zen-primary font-bold font-mono"><Hash className="w-3 h-3" />{u.pager}</span>}
                      {u.telegram_id && <span className="inline-flex items-center gap-1 text-zen-muted"><Send className="w-3 h-3" /> {u.telegram_id}</span>}
                    </div>
                  )}
                  <div className="mt-3 flex flex-wrap gap-1">
                    {u.skills.map((sid) => { const sv = services.find((x) => x.id === sid); return sv ? <span key={sid} className="text-xs px-2 py-0.5 rounded-full bg-zen-surface">{sv.name}</span> : null; })}
                    {u.skills.length === 0 && <span className="text-xs text-zen-muted">No skills</span>}
                  </div>
                </div>
                <div className="flex flex-col gap-1">
                  <Button size="icon" variant="ghost" onClick={() => { setEditing(u); setOpen(true); }} data-testid={`edit-staff-${u.id}`}><Pencil className="w-4 h-4" /></Button>
                  {isOwner && (
                    <Button size="icon" variant="ghost" onClick={() => remove(u)}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      data-testid={`delete-staff-${u.id}`}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
      <EditDialog open={open} onClose={() => setOpen(false)} user={editing} services={services} onSave={save} isOwner={isOwner} />
      <CreateDialog open={createOpen} onClose={() => setCreateOpen(false)} services={services} onSave={create} />
      {/* Import from Turn Board — picks up staff_info rows that have no
          matching Booking user, and creates user accounts for them. */}
      <Dialog open={importOpen} onOpenChange={setImportOpen}>
        <DialogContent className="max-w-lg" data-testid="import-tb-dialog">
          <DialogHeader>
            <DialogTitle>Import staff from Turn Board</DialogTitle>
          </DialogHeader>
          {orphans.length === 0 ? (
            <div className="text-sm text-zen-muted py-4" data-testid="import-tb-empty">
              ✓ Every staff in your Turn Board already has a Booking account.
            </div>
          ) : (
            <div className="space-y-3">
              <div className="text-xs text-zen-muted">
                These technicians exist on the Turn Board but are missing from Booking.
                Tick the ones you want to create Booking accounts for. Username is auto-generated from the name.
              </div>
              <div className="max-h-80 overflow-y-auto border border-zen-border rounded-lg divide-y divide-zen-border">
                {orphans.map((o) => (
                  <label key={o.pager} className="flex items-center gap-3 p-3 hover:bg-zen-surface cursor-pointer" data-testid={`orphan-row-${o.pager}`}>
                    <input
                      type="checkbox"
                      checked={!!selectedOrphans[o.pager]}
                      onChange={(e) => setSelectedOrphans((p) => ({ ...p, [o.pager]: e.target.checked }))}
                      className="w-4 h-4"
                      data-testid={`orphan-check-${o.pager}`}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-zen-ink">{o.name}</div>
                      <div className="text-xs text-zen-muted">Pager #{o.pager}{o.telegram_id ? ` · Telegram ${o.telegram_id}` : ""}</div>
                    </div>
                  </label>
                ))}
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-zen-ink">Default Password (set for all imported staff)</label>
                <Input
                  value={importPassword}
                  onChange={(e) => setImportPassword(e.target.value)}
                  className="bg-zen-surface border-0 mt-1"
                  data-testid="import-tb-password"
                />
                <div className="text-[11px] text-zen-muted mt-1">
                  Share this with each tech so they can log in. They can change it later.
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setImportOpen(false)}>Cancel</Button>
            {orphans.length > 0 && (
              <Button onClick={runImport} disabled={importBusy} className="bg-zen-primary text-white" data-testid="import-tb-confirm">
                {importBusy ? "Importing…" : `Import ${Object.values(selectedOrphans).filter(Boolean).length} staff`}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}

function EditDialog({ open, onClose, user, services, onSave, isOwner }) {
  const [form, setForm] = useState({ full_name: "", skills: [], active: true, password: "", linked_staff_ids: [], sex: null, work_start: "", work_end: "", off_weekdays: [], pager: "", telegram_id: "" });
  const [allStaff, setAllStaff] = useState([]);
  useEffect(() => {
    if (user) setForm({
      full_name: user.full_name, skills: user.skills || [], active: user.active,
      password: "", linked_staff_ids: user.linked_staff_ids || [], sex: user.sex || null,
      work_start: user.work_start || "", work_end: user.work_end || "",
      off_weekdays: user.off_weekdays || [],
      pager: user.pager || "", telegram_id: user.telegram_id || "",
    });
  }, [user, open]);
  useEffect(() => {
    if (open) api.get("/admin/users").then(({ data }) => setAllStaff(data.filter((x) => x.role === "staff")));
  }, [open]);
  const toggleSkill = (sid) => setForm({ ...form, skills: form.skills.includes(sid) ? form.skills.filter((x) => x !== sid) : [...form.skills, sid] });
  const toggleLink = (uid) => setForm({ ...form, linked_staff_ids: form.linked_staff_ids.includes(uid) ? form.linked_staff_ids.filter((x) => x !== uid) : [...form.linked_staff_ids, uid] });
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="staff-edit-dialog">
        <DialogHeader><DialogTitle>Edit Staff</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <Input placeholder="Full Name" value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} className="bg-zen-surface border-0" data-testid="staff-edit-name" />
          <div className="grid grid-cols-2 gap-2">
            <div>
              <div className="text-[10px] uppercase tracking-wider text-zen-muted mb-1 font-semibold">Pager # <span className="text-zen-cal-red">*</span></div>
              <Input placeholder="e.g. 101" value={form.pager}
                onChange={(e) => setForm({ ...form, pager: e.target.value.replace(/\D/g, "").slice(0, 4) })}
                className="bg-zen-surface border-0 font-mono text-lg tracking-widest" data-testid="staff-edit-pager" />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider text-zen-muted mb-1 font-semibold">Telegram ID <span className="text-zen-cal-red">*</span></div>
              <Input placeholder="@username or numeric id" value={form.telegram_id}
                onChange={(e) => setForm({ ...form, telegram_id: e.target.value })}
                className="bg-zen-surface border-0" data-testid="staff-edit-telegram" />
            </div>
          </div>
          <div>
            <div className="text-xs uppercase text-zen-muted mb-1">Gender</div>
            <div className="inline-flex rounded-full border border-zen-border p-0.5 bg-white">
              <button type="button" onClick={() => setForm({ ...form, sex: "male" })}
                className={`px-4 h-9 rounded-full text-xs font-medium ${form.sex === "male" ? "bg-zen-primary text-white" : "text-zen-muted"}`}
                data-testid="staff-sex-male">♂ Male</button>
              <button type="button" onClick={() => setForm({ ...form, sex: "female" })}
                className={`px-4 h-9 rounded-full text-xs font-medium ${form.sex === "female" ? "bg-zen-cal-red text-white" : "text-zen-muted"}`}
                data-testid="staff-sex-female">♀ Female</button>
            </div>
          </div>
          <div className="text-xs uppercase text-zen-muted">Skills</div>
          <div className="flex flex-wrap gap-2 max-h-48 overflow-y-auto">
            {services.map((s) => { const checked = form.skills.includes(s.id); return (
              <button key={s.id} onClick={() => toggleSkill(s.id)} className={`px-3 py-1.5 rounded-full text-xs border ${checked ? "bg-zen-primary text-white border-zen-primary" : "bg-white border-zen-border"}`} data-testid={`skill-toggle-${s.id}`}>{s.name}</button>
            ); })}
          </div>
          <div>
            <div className="text-xs uppercase text-zen-muted mb-1">Linked Account (share login)</div>
            <div className="text-[11px] text-zen-muted mb-2">Pair with another staff (e.g. husband/wife) — they will share this login and see each other's schedule.</div>
            <div className="flex flex-wrap gap-2">
              {allStaff.filter((s) => s.id !== user?.id).map((s) => {
                const checked = form.linked_staff_ids.includes(s.id);
                return (
                  <button key={s.id} onClick={() => toggleLink(s.id)}
                    className={`px-3 py-1.5 rounded-full text-xs border ${checked ? "bg-zen-cal-green text-white border-zen-cal-green" : "bg-white border-zen-border"}`}
                    data-testid={`link-toggle-${s.id}`}>
                    {s.full_name}
                  </button>
                );
              })}
              {allStaff.filter((s) => s.id !== user?.id).length === 0 && (
                <span className="text-xs text-zen-muted">No other staff to link</span>
              )}
            </div>
          </div>
          <div>
            <div className="text-xs uppercase text-zen-muted mb-1">Custom Work Hours</div>
            <div className="text-[11px] text-zen-muted mb-2">Override shop hours. Leave blank to use shop hours. E.g. Bibi starts at 11:00 AM → calendar blocks 9:00 AM – 11:00 AM for her.</div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <div className="text-[10px] text-zen-muted mb-1">Start</div>
                <TimePicker value={form.work_start} onChange={(v) => setForm({ ...form, work_start: v })} placeholder="-- : --" testIdPrefix="staff-work-start" />
              </div>
              <div>
                <div className="text-[10px] text-zen-muted mb-1">End</div>
                <TimePicker value={form.work_end} onChange={(v) => setForm({ ...form, work_end: v })} placeholder="-- : --" testIdPrefix="staff-work-end" />
              </div>
            </div>
            {(form.work_start || form.work_end) && (
              <button type="button" onClick={() => setForm({ ...form, work_start: "", work_end: "" })}
                className="mt-2 text-[11px] text-zen-muted hover:text-zen-cal-red underline" data-testid="clear-work-hours">
                Clear (use shop hours)
              </button>
            )}
          </div>
          {isOwner && (
            <div className="border-t border-zen-border pt-3">
              <div className="text-xs uppercase text-zen-muted mb-1">Weekly Days Off <span className="normal-case font-normal text-zen-cal-red">(Owner only)</span></div>
              <div className="text-[11px] text-zen-muted mb-2">Block recurring days. Staff cannot edit this. E.g. Bibi off every Tuesday → calendar blocks every Tuesday for her, online customers cannot book her on Tuesdays.</div>
              <div className="flex flex-wrap gap-1.5">
                {WEEKDAYS.map((d) => {
                  const checked = form.off_weekdays.includes(d.val);
                  return (
                    <button key={d.val} type="button"
                      onClick={() => setForm({ ...form, off_weekdays: checked ? form.off_weekdays.filter((x) => x !== d.val) : [...form.off_weekdays, d.val] })}
                      className={`px-3 py-1.5 rounded-full text-xs border-2 ${checked ? "bg-zen-cal-red text-white border-zen-cal-red" : "bg-white border-zen-border"}`}
                      data-testid={`off-weekday-${d.val}`}>
                      {d.label}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
          <Input type="password" placeholder="New password (optional)" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="bg-zen-surface border-0" data-testid="staff-reset-password" />
          <div className="flex items-center justify-between p-3 rounded-lg bg-zen-surface">
            <span className="text-sm">Active</span><Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} data-testid="staff-active" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={() => onSave({ ...form, password: form.password || undefined })} className="bg-zen-primary text-white" data-testid="staff-edit-save">Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CreateDialog({ open, onClose, services, onSave }) {
  const [form, setForm] = useState({ username: "", password: "", full_name: "", skills: [], sex: null, pager: "", telegram_id: "" });
  useEffect(() => { if (open) setForm({ username: "", password: "", full_name: "", skills: [], sex: null, pager: "", telegram_id: "" }); }, [open]);
  const toggleSkill = (sid) => setForm({ ...form, skills: form.skills.includes(sid) ? form.skills.filter((x) => x !== sid) : [...form.skills, sid] });
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="staff-create-dialog">
        <DialogHeader><DialogTitle>Add Staff</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <Input placeholder="Username" value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} className="bg-zen-surface border-0" data-testid="staff-create-username" />
          <Input type="password" placeholder="Password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="bg-zen-surface border-0" data-testid="staff-create-password" />
          <Input placeholder="Full Name" value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} className="bg-zen-surface border-0" data-testid="staff-create-name" />
          <div className="grid grid-cols-2 gap-2">
            <div>
              <div className="text-[10px] uppercase tracking-wider text-zen-muted mb-1 font-semibold">Pager # <span className="text-zen-cal-red">*</span> required</div>
              <Input placeholder="e.g. 101" value={form.pager}
                onChange={(e) => setForm({ ...form, pager: e.target.value.replace(/\D/g, "").slice(0, 4) })}
                className="bg-zen-surface border-0 font-mono text-lg tracking-widest" data-testid="staff-create-pager" />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider text-zen-muted mb-1 font-semibold">Telegram ID <span className="text-zen-cal-red">*</span> required</div>
              <Input placeholder="@username" value={form.telegram_id}
                onChange={(e) => setForm({ ...form, telegram_id: e.target.value })}
                className="bg-zen-surface border-0" data-testid="staff-create-telegram" />
            </div>
          </div>
          <div>
            <div className="text-xs uppercase text-zen-muted mb-1">Gender</div>
            <div className="inline-flex rounded-full border border-zen-border p-0.5 bg-white">
              <button type="button" onClick={() => setForm({ ...form, sex: "male" })}
                className={`px-4 h-9 rounded-full text-xs font-medium ${form.sex === "male" ? "bg-zen-primary text-white" : "text-zen-muted"}`}
                data-testid="create-sex-male">♂ Male</button>
              <button type="button" onClick={() => setForm({ ...form, sex: "female" })}
                className={`px-4 h-9 rounded-full text-xs font-medium ${form.sex === "female" ? "bg-zen-cal-red text-white" : "text-zen-muted"}`}
                data-testid="create-sex-female">♀ Female</button>
            </div>
          </div>
          <div className="flex flex-wrap gap-2 max-h-48 overflow-y-auto">
            {services.map((s) => { const checked = form.skills.includes(s.id); return (
              <button key={s.id} onClick={() => toggleSkill(s.id)} className={`px-3 py-1.5 rounded-full text-xs border ${checked ? "bg-zen-primary text-white border-zen-primary" : "bg-white border-zen-border"}`} data-testid={`create-skill-${s.id}`}>{s.name}</button>
            ); })}
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={() => onSave(form)} className="bg-zen-primary text-white" data-testid="staff-create-save">Create</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
